import { Box, Link, Stack, Typography } from '@mui/material';
import { alpha, type SxProps, type Theme } from '@mui/material/styles';
import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { useDisplayTime } from 'src/utils/hooks/use-display-time';
import { useSmsSessionContext } from './sms-session-context';
import { buildThreadEntries, type SmsSessionGroup } from './sms-session-grouping';

const PIN_TO_BOTTOM_THRESHOLD_PX = 48;

const AVATAR_SX: SxProps<Theme> = {
  width: '1.125rem',
  height: '1.125rem',
  '&[data-length]': { fontSize: '0.5625rem' },
};

const headerTimeSx: SxProps<Theme> = {
  color: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
};

const bubbleSx = (isOutbound: boolean): SxProps<Theme> => ({
  px: 1.25,
  py: 0.75,
  borderRadius: 1.5,
  maxWidth: '80%',
  backgroundColor: (theme) =>
    isOutbound ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.08),
  color: (theme) =>
    isOutbound ? theme.palette.primary.contrastText : theme.palette.text.primary,
});

const attachmentLinkSx: SxProps<Theme> = {
  display: 'block',
  color: 'inherit',
  textDecorationColor: 'inherit',
  fontSize: (theme) => theme.typography.body2.fontSize,
  wordBreak: 'break-all',
};

const failedCaptionSx: SxProps<Theme> = {
  color: 'error.main',
  fontSize: '0.6875rem',
};

const HTML_TAG = /<\/?[a-z][^>]*>/i;

function toPlainText(body: string): string {
  if (!HTML_TAG.test(body)) return body;
  const html = body.replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '</p>\n');
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').trim();
}

function usePinToNewest(enabled: boolean, lastMessageId: string | undefined) {
  const containerRef = useRef<HTMLDivElement>(null);
  const isPinnedRef = useRef(true);
  const hasScrolledOnceRef = useRef(false);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    isPinnedRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < PIN_TO_BOTTOM_THRESHOLD_PX;
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!enabled || !el || !isPinnedRef.current) return;
    if (typeof el.scrollTo === 'function') {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: hasScrolledOnceRef.current ? 'smooth' : 'auto',
      });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    hasScrolledOnceRef.current = true;
  }, [enabled, lastMessageId]);

  return { containerRef, handleScroll: enabled ? handleScroll : undefined };
}

/** Avatar · name · time in that order on both sides, so the two columns read the same. */
const SenderHeader = ({ name, time }: { name: string | null; time: string }) => (
  <Stack direction="row" alignItems="center" gap={0.5}>
    {name ? <ColoredAvatar name={name} sx={AVATAR_SX} /> : null}
    {name ? (
      <Typography fontWeight="bold" fontSize="0.6875rem">
        {name}
      </Typography>
    ) : null}
    <Typography fontWeight="bold" fontSize="0.6875rem" sx={headerTimeSx}>
      {name ? `· ${time}` : time}
    </Typography>
  </Stack>
);

export const SmsSessionThread = ({
  group,
  maxHeight,
  pinToNewest = false,
}: {
  group: SmsSessionGroup;
  /** Set only where the thread lives in a fixed-height box (the docked live panel). In the
   *  timeline it must flow: a scroller nested in the chat feed swallows the feed's own
   *  scroll, which on touch leaves nothing to grab. */
  maxHeight?: string;
  pinToNewest?: boolean;
}) => {
  const sendersByChannelId = useSmsSessionContext((v) => v.sendersByChannelId);
  const { displayTime } = useDisplayTime();

  const entries = useMemo(() => buildThreadEntries(group), [group]);
  const lastMessageId = group.messages.at(-1)?.conversationMessageId;
  const { containerRef, handleScroll } = usePinToNewest(pinToNewest, lastMessageId);

  return (
    <Stack
      ref={containerRef}
      onScroll={handleScroll}
      /* Shrinking matters in the dock: squeezed by the card below, a scroller that keeps its
         full height gets clipped by the panel, and the newest messages it pins to land
         outside the visible box — which reads as "the session does not scroll". */
      sx={{
        px: 1.5,
        py: 1.25,
        gap: 0.5,
        ...(maxHeight ? { maxHeight, flexShrink: 1, minHeight: 0, overflowY: 'auto' } : null),
      }}
    >
      {entries.map(({ message, isOutbound, showHeader }, index) => {
        const name = message.senderChannelId
          ? (sendersByChannelId.get(message.senderChannelId) ?? null)
          : null;
        const text = toPlainText(message.body ?? '');
        const attachments = message.attachments ?? [];

        return (
          <Stack
            key={message.conversationMessageId}
            alignItems={isOutbound ? 'flex-end' : 'flex-start'}
            gap={0.5}
            sx={{ mt: showHeader && index > 0 ? 0.75 : 0 }}
          >
            {showHeader ? (
              <SenderHeader name={name} time={displayTime(message.createdAt)} />
            ) : null}
            <Box sx={bubbleSx(isOutbound)}>
              {text ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {text}
                </Typography>
              ) : null}
              {attachments.map((attachment) =>
                attachment.url ? (
                  <Link
                    key={attachment.fileName}
                    href={attachment.url}
                    target="_blank"
                    rel="noreferrer"
                    sx={attachmentLinkSx}
                  >
                    {attachment.fileName}
                  </Link>
                ) : (
                  <Typography key={attachment.fileName} variant="body2">
                    {attachment.fileName}
                  </Typography>
                ),
              )}
            </Box>
            {/* Outbound only: inbound messages can carry failed dispatch legs for OTHER
                channels (e.g. a parallel email leg), which say nothing about the SMS. */}
            {isOutbound && message.dispatchFailed ? (
              <Typography sx={failedCaptionSx}>
                Not delivered
                {message.dispatchFailureReason ? ` — ${message.dispatchFailureReason}` : ''}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
};
